import { createContext, useContext, useState, useCallback } from 'react';
import { toast } from 'sonner';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem('cart_items');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  const saveItems = useCallback((newItems) => {
    setItems(newItems);
    localStorage.setItem('cart_items', JSON.stringify(newItems));
  }, []);

  const addItem = useCallback((product) => {
    setItems(prev => {
      const exists = prev.find(i => i.slug === product.slug);
      if (exists) {
        toast.info(`Already in cart: ${product.name}`, { duration: 2200 });
        return prev;
      }
      const updated = [...prev, { slug: product.slug, name: product.name, price: product.price, image_url: product.image_url, platform: product.platform }];
      localStorage.setItem('cart_items', JSON.stringify(updated));
      toast.success(`Added to cart: ${product.name}`, {
        description: `$${product.price} · Click cart to checkout`,
        duration: 2600,
        action: {
          label: 'View cart',
          onClick: () => { window.location.href = '/cart'; },
        },
      });
      return updated;
    });
  }, []);

  const removeItem = useCallback((slug) => {
    setItems(prev => {
      const removed = prev.find(i => i.slug === slug);
      const updated = prev.filter(i => i.slug !== slug);
      localStorage.setItem('cart_items', JSON.stringify(updated));
      if (removed) toast.info(`Removed: ${removed.name}`, { duration: 2000 });
      return updated;
    });
  }, []);

  const clearCart = useCallback(() => {
    saveItems([]);
    toast.info('Cart cleared', { duration: 1800 });
  }, [saveItems]);

  const total = items.reduce((sum, i) => sum + i.price, 0);
  const count = items.length;

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, clearCart, total, count }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
